import { z } from 'zod';
import { getJson } from './http';
import { readKvBlob, writeKvBlob } from './kvBlob';
import { createScopedBlobStore } from './scopedBlobStore';
import type { DividendPoint } from './calc/types';
import { resolveStartingCash } from './calc/holdings';
import { TRADE_TYPES } from './calc/tradeTypes';

export { DEFAULT_STARTING_CASH } from './calc/holdings';

/**
 * Typed client for /api/data/:key — the KV-backed storage endpoint. The React
 * app fetches blobs directly and computes derived data client-side.
 *
 * Multi-portfolio: the trades blob can be EITHER a plain array (old single-
 * portfolio format) OR { [portfolioId]: Trade[] }; getTrades handles both.
 */

// The read/write ceremony and the error type live in the kvBlob seam;
// re-exported here so existing imports keep working.
export { PortfolioApiError } from './kvBlob';

export const TradeSchema = z.object({
  id: z.string().optional(),
  date: z.string(),
  // Optional HH:MM:SS (24h): same-day rows with times replay in time order
  // (replayCompare) instead of the type-order heuristic alone.
  time: z.string().optional(),
  ticker: z.string(),
  name: z.string().optional(),
  sector: z.string().optional(),
  country: z.string().optional(),
  // THE one list (calc/tradeTypes.ts), which the edge's write guard reads too.
  type: z.enum(TRADE_TYPES),
  shares: z.number(),
  price: z.number(),
  // Cash magnitude (positive) of an amount row: DEPOSIT, WITHDRAWAL, FEE,
  // INTEREST, TAX. BUY/SELL/DIVIDEND leave this undefined and derive their
  // cash impact from shares*price.
  amount: z.number().optional(),
  // A FEE or TAX row that gives the cost back (+amount, inside the return).
  refund: z.boolean().optional(),
  // A TAX row: withholding a treaty lets the owner claim back. Labelled only;
  // nothing accrues the claim before it is paid.
  reclaimable: z.boolean().optional(),
  // A TAX row: the date of the income payment it was withheld from, when that
  // is not the tax row's own date (calc/withholding.ts links the two).
  incomeDate: z.string().optional(),
  commission: z.number().optional(),
  // Per-trade currency; falls back to the portfolio base at render time.
  currency: z.string().optional(),
  thesis: z.string().optional(),
  sentiment: z.enum(['Bullish', 'Neutral', 'Bearish']).optional(),
  conviction: z.number().int().min(0).max(5).optional(),
  tags: z.array(z.string()).optional(),
  targetPrice: z.number().optional(),
  stopLoss: z.number().optional(),
  journalLinkId: z.string().optional(),
}).loose();
export type Trade = z.infer<typeof TradeSchema>;

// Trades persist as a scoped blob (array legacy / {portfolioId: array} multi).
const tradesStore = createScopedBlobStore('trades', TradeSchema);

export const CustomIndexSchema = z.object({
  ticker: z.string(),
  name: z.string().optional(),
  color: z.string().optional(),
});
export type CustomIndex = z.infer<typeof CustomIndexSchema>;

// The portfolio's TARGET allocation (WORK.md item 45, D21): ticker → weight,
// what the drift check judges the book against. `source` says how it was set
// (copied from the holdings, from the optimiser's suggestion, or accepted from
// the model's on the Model tab, item 57).
export const RebalanceTargetSchema = z.object({
  weights: z.record(z.string(), z.number()),
  source: z.enum(['holdings', 'optimiser', 'model']).optional(),
  setAt: z.string().optional(),
}).loose();
export type RebalanceTarget = z.infer<typeof RebalanceTargetSchema>;

// The drift check's bars, per portfolio (D21). Absent fields take the owner's
// defaults: 3 pp a year at 70 % confidence (return), 2 pp a year (risk).
export const RebalanceThresholdsSchema = z.object({
  returnThreshold: z.number().optional(),
  confidence: z.number().optional(),
  riskThreshold: z.number().optional(),
}).loose();
export type RebalanceThresholds = z.infer<typeof RebalanceThresholdsSchema>;

export const PortfolioRefSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  startingCash: z.number().optional(),
  customIndexes: z.array(CustomIndexSchema).optional(),
  benchmarks: z.array(z.string()).optional(),
  // Per-portfolio base currency + risk-free rate; default-to-top-level handled
  // by the resolve* helpers below, so blobs that don't set these keep working.
  baseCurrency: z.string().optional(),
  riskFreeRate: z.number().optional(),
  rebalanceTarget: RebalanceTargetSchema.optional(),
  rebalanceThresholds: RebalanceThresholdsSchema.optional(),
  // This portfolio's own model-portfolio settings (WORK.md item 57), a partial
  // block over the person's `modelDefaults` (lib/modelSettings.ts). Loose so a
  // bad field never fails the whole parse: `resolveModelSettings` leaves it out
  // and says so; the edge refuses one on write (`modelSettingsProblem`).
  modelSettings: z.unknown().optional(),
}).loose();
export type PortfolioRef = z.infer<typeof PortfolioRefSchema>;

// Maps a source instrument key to how the importer should track it: self-priced,
// a live ticker, or an ETF proxy. Persisted in settings so repeat imports auto-resolve.
export const ImportAliasSchema = z.object({
  mode: z.enum(['self', 'live', 'proxy']),
  ticker: z.string(),
  isin: z.string().optional(),
  name: z.string().optional(),
});
export type ImportAlias = z.infer<typeof ImportAliasSchema>;

// Country/sector weight maps for a single ticker. Values are PERCENTAGES (0–100)
// for editor friendliness; consumers normalise to fractions.
export const AllocationOverrideSchema = z.object({
  country: z.record(z.string(), z.number()).optional(),
  sector: z.record(z.string(), z.number()).optional(),
}).loose();
export type AllocationOverride = z.infer<typeof AllocationOverrideSchema>;

// One warehouse match: how an app symbol maps to the EODHD instrument.
export const WarehouseLinkSchema = z.object({
  isin: z.string().optional(),
  ticker: z.string().optional(),
  name: z.string().optional(),
}).loose();
export type WarehouseLink = z.infer<typeof WarehouseLinkSchema>;

export const SettingsSchema = z.object({
  startingCash: z.number().optional(),
  baseCurrency: z.string().optional(),
  riskFreeRate: z.number().optional(),
  portfolios: z.array(PortfolioRefSchema).optional(),
  activePortfolioId: z.string().optional(),
  importAliases: z.record(z.string(), ImportAliasSchema).optional(),
  // Per-symbol price-source override: held symbol -> the ticker/series symbol
  // whose prices it should use. Absent = self-priced as-is.
  priceLinks: z.record(z.string(), z.string()).optional(),
  // Projection proxy map: held (typically self-priced) symbol -> the artefact
  // instrument whose risk it borrows in the Projections tab and its rebalance
  // runs. Shared across portfolios like priceLinks — a proxy is a property of
  // the instrument, not of the book holding it. The Views panel writes it.
  // (The nightly rebalance digest that also read it went with WORK.md item 45
  // D19, 2026-09-27.)
  projectionProxies: z.record(z.string(), z.string()).optional(),
  // The owner's assumptions for a holding with no market data: annual return,
  // annual volatility and ρ to the rest of the book, per symbol and shared
  // across portfolios like the proxies beside it (lib/projectionAssumptions.ts).
  // Loose numbers on purpose: an out-of-range value is clamped where it is
  // used, never allowed to fail the parse of the whole settings blob.
  projectionAssumptions: z.record(z.string(), z.object({
    mu: z.number().optional(), vol: z.number().optional(), rho: z.number().optional(),
  })).optional(),
  // The owner's Black-Litterman views, per PORTFOLIO (a view names holdings of
  // one book, so unlike the proxies it is not shared): portfolio id -> views
  // (lib/projectionViews.ts). Loose like the assumptions: a malformed view is
  // dropped where it is read, never allowed to fail the whole settings blob.
  projectionViews: z.record(z.string(), z.array(z.object({
    kind: z.string().optional(), asset: z.string().optional(),
    asset_short: z.string().nullable().optional(),
    value: z.number().optional(), confidence: z.number().optional(),
  }).loose())).optional(),
  // The owner's alert rules (lib/alertRules.ts), checked on Performance
  // Summary. Loose: a rule saved before the kinds were validated is kept and
  // shown as "not checked" with its reason, never allowed to fail the parse.
  alertRules: z.array(z.object({
    id: z.string().optional(), type: z.string().optional(), ticker: z.string().optional(),
    threshold: z.number().optional(), tolerance: z.number().optional(), days: z.number().optional(),
  }).loose()).optional(),
  // The owner's import templates (a confirmed column mapping keyed by the
  // file's header signature) and type rules (text → trade type), read by the
  // Import page through lib/importRules.ts's `readTemplates` / `readRules`,
  // which leave a malformed entry out. Loose here so one bad entry never fails
  // the whole settings parse; the edge refuses a malformed one on write
  // (`importSettingsProblem`). `importAiDisabled` hides "Suggest with AI".
  importTemplates: z.array(z.unknown()).optional(),
  importTypeRules: z.array(z.unknown()).optional(),
  importAiDisabled: z.boolean().optional(),
  // The owner's optional dividend tax for MARKET series (benchmarks, research
  // tickers, comparison lines), { enabled, ratePct }: off unless turned on,
  // never applied to the book (lib/dividendTaxSetting.ts). Loose here so a
  // malformed value reads as off rather than failing the whole settings
  // parse; the edge refuses a malformed one on write.
  marketDividendTax: z.unknown().optional(),
  // The person's default model-portfolio settings, which each portfolio's own
  // `modelSettings` overrides field by field (WORK.md item 57,
  // lib/modelSettings.ts; the strict schema is app/src/lib/modelSettings.ts's
  // `ModelSettingsBlockSchema`). Loose here for the same reason as above.
  modelDefaults: z.unknown().optional(),
  // Per-ticker country/sector allocation splits (look-through weights), keyed by
  // ticker and SHARED across portfolios. Each sub-map is { label -> percentage };
  // weights are normalised at aggregation time so they need not sum to 100.
  allocationOverrides: z.record(z.string(), AllocationOverrideSchema).optional(),
  // Live broker sync routing: SnapTrade account id -> portfolio id it last
  // imported into. The Import page preselects the saved destination.
  snapAccountDest: z.record(z.string(), z.string()).optional(),
  // EODHD warehouse match registry: app symbol -> the warehouse instrument its
  // daily values come from (ISIN preferred, else listing ticker).
  warehouseLinks: z.record(z.string(), WarehouseLinkSchema).optional(),
  // Default benchmark tickers shown for portfolios without their own list.
  defaultBenchmarks: z.array(z.string()).optional(),
}).loose();
export type Settings = z.infer<typeof SettingsSchema>;
/**
 * Resolve the base currency for a portfolio: per-portfolio value first, then
 * top-level settings.baseCurrency (legacy), ultimately 'USD'.
 */
export function resolvePortfolioCurrency(
  settings: Settings,
  portfolioId?: string,
): string {
  const p = (settings.portfolios ?? []).find((x) => x.id === portfolioId);
  if (p && typeof p.baseCurrency === 'string' && p.baseCurrency.length > 0) {
    return p.baseCurrency;
  }
  return settings.baseCurrency ?? 'USD';
}

/**
 * Resolve a portfolio's opening cash — `resolveStartingCash` in
 * lib/calc/holdings.ts, the one rule the app and the edge's shared view share
 * (its docstring has the history). Every page reads it through this name.
 */
export function resolvePortfolioStartingCash(settings: Settings, portfolioId?: string): number {
  return resolveStartingCash(settings, portfolioId);
}

/**
 * Resolve the risk-free rate for a portfolio (same precedence as currency).
 * Returns the rate as STORED — Settings stores percent (4.0 = 4%); every
 * consumer normalises via `_rfRaw > 1 ? _rfRaw / 100 : _rfRaw`.
 */
export function resolvePortfolioRiskFreeRate(
  settings: Settings,
  portfolioId?: string,
): number {
  return resolvePortfolioRiskFreeRateSource(settings, portfolioId).raw;
}

/** Where `resolvePortfolioRiskFreeRate`'s answer came from. */
export interface RiskFreeRateSource {
  /** The rate as STORED (percent, or a legacy decimal): normalise it. */
  raw: number;
  /** The portfolio's own setting, the global one, or the app's 4% default. */
  source: 'portfolio' | 'global' | 'default';
  /** The portfolio's name when `source` is 'portfolio'. */
  portfolioName?: string;
}

/**
 * The same precedence as `resolvePortfolioRiskFreeRate`, saying which setting
 * answered, so a page that defaults a field to the book's rate can show where
 * the default came from.
 */
export function resolvePortfolioRiskFreeRateSource(
  settings: Settings,
  portfolioId?: string,
): RiskFreeRateSource {
  const p = (settings.portfolios ?? []).find((x) => x.id === portfolioId);
  if (p && typeof p.riskFreeRate === 'number' && Number.isFinite(p.riskFreeRate)) {
    return { raw: p.riskFreeRate, source: 'portfolio', portfolioName: p.name || p.id };
  }
  if (typeof settings.riskFreeRate === 'number' && Number.isFinite(settings.riskFreeRate)) {
    return { raw: settings.riskFreeRate, source: 'global' };
  }
  return { raw: 4.0, source: 'default' };
}


export type PriceStore = Record<string, Record<string, number>>;

// { [symbol]: { 'YYYY-MM-DD': close } } — user-supplied closes for instruments
// with no live feed (e.g. unit-linked pension funds imported from a statement).
export const CustomPricesSchema = z.record(z.string(), z.record(z.string(), z.number()));
export type CustomPrices = z.infer<typeof CustomPricesSchema>;

// Global Indexes page catalog — the user-editable ETF category list. An absent
// blob means "never edited": the page seeds from Config.GLOBAL_INDEXES, and the
// nightly quote mirror falls back to its shipped copy of that seed.
export const GlobalIndexesSchema = z.array(z.object({
  name: z.string().min(1),
  etfs: z.array(z.object({
    ticker: z.string().min(1),
    name: z.string(),
    tags: z.array(z.string()).default([]),
  })),
}));
export type GlobalIndexCategories = z.infer<typeof GlobalIndexesSchema>;

export function getGlobalIndexes(): Promise<GlobalIndexCategories> {
  return readKvBlob('globalIndexes', GlobalIndexesSchema, []);
}

export function saveGlobalIndexes(categories: GlobalIndexCategories): Promise<void> {
  return writeKvBlob('globalIndexes', categories);
}

/**
 * Get trades for one portfolio. Handles both the old array format and the new
 * {[portfolioId]: trades} object format transparently.
 */
export function getTrades(portfolioId?: string): Promise<Trade[]> {
  return tradesStore.get(portfolioId);
}

export async function getSettings(): Promise<Settings> {
  return readKvBlob('settings', SettingsSchema, {});
}

/** Self-priced instrument closes written by the flexible importer. */
export function getCustomPrices(): Promise<CustomPrices> {
  return readKvBlob('customPrices', CustomPricesSchema, {});
}

/**
 * USD-pivot FX rate store (currency → date → USD per 1 unit), built from the
 * data Worker's /api/symbols/fx and held for the session (fxStoreCache.ts,
 * WORK.md item 52 step A2). A currency with no series is absent, and lib/fx
 * answers null for it, never 1; single-currency portfolios need no rate.
 */
export { getFxStore } from './fxStoreCache';

/** One cash dividend as the treatment consumes it: ex-date + per-share amount
 *  (as declared only from a raw-v1 or derived-v1 feed; see calc/types DividendFeed). */
export type { DividendPoint };

// Market-series reading (live price, history, candles, dividends, the treatment
// and FX transforms) moved to seriesApi.ts — this module is the KV blob and
// settings client. Re-exported so no importer changed.
export {
  fetchLivePrice, fetchPriceHistory, fetchCandleBars, fetchDividends,
  fetchTreatedHistory, convertSeriesToBase, fetchAdjustedOrTreatedHistory,
  fetchBenchmarkHistory,
} from './seriesApi';
export type { ServerHistoryBar, DividendBasisSink } from './seriesApi';

/**
 * Persist the trade list for a portfolio. POSTs the FULL trades blob back,
 * preserving both legacy formats. Single-portfolio users (no portfolioId) keep
 * the old array shape so the legacy app keeps reading it unchanged. Throws
 * PortfolioApiError on any non-2xx.
 */
export function saveTrades(trades: Trade[], portfolioId?: string): Promise<void> {
  return tradesStore.save(trades, portfolioId);
}

/**
 * Append trades without clobbering rows added concurrently (by Import, another
 * tab, or another device). Re-reads the stored slice inside a per-key queue and
 * appends, rather than replacing the slice from a possibly-stale in-memory copy.
 * Returns the portfolio's full slice after the append.
 */
export function appendTrades(newRows: Trade[], portfolioId?: string): Promise<Trade[]> {
  return tradesStore.append(newRows, portfolioId);
}

export const WatchlistItemSchema = z.object({
  id: z.string().optional(),
  ticker: z.string(),
  name: z.string().optional(),
  notes: z.string().optional(),
  addedAt: z.string().optional(),
}).loose();
export type WatchlistItem = z.infer<typeof WatchlistItemSchema>;

const watchlistStore = createScopedBlobStore('watchlist', WatchlistItemSchema);

export function getWatchlist(portfolioId?: string): Promise<WatchlistItem[]> {
  return watchlistStore.get(portfolioId);
}

/** Every portfolio's watchlist items, one list: an item watched in two portfolios is in it twice. */
export function getAllWatchlistItems(): Promise<WatchlistItem[]> {
  return watchlistStore.all();
}

export function saveWatchlist(items: WatchlistItem[], portfolioId?: string): Promise<void> {
  return watchlistStore.save(items, portfolioId);
}

export const SnapshotHoldingSchema = z.object({
  ticker: z.string(),
  shares: z.number(),
  marketValue: z.number().optional(),
  weight: z.number().optional(),
}).loose();

export const SnapshotSchema = z.object({
  id: z.string().optional(),
  date: z.string(),
  totalValue: z.number(),
  totalReturn: z.number().optional(),
  cash: z.number().optional(),
  positionCount: z.number().optional(),
  sectorCount: z.number().optional(),
  isMonthly: z.boolean().optional(),
  holdings: z.array(SnapshotHoldingSchema).optional(),
  sectors: z.array(z.unknown()).optional(),
}).loose();
export type Snapshot = z.infer<typeof SnapshotSchema>;

const snapshotsStore = createScopedBlobStore('snapshots', SnapshotSchema);

export function getSnapshots(portfolioId?: string): Promise<Snapshot[]> {
  return snapshotsStore.get(portfolioId);
}

export function saveSnapshots(items: Snapshot[], portfolioId?: string): Promise<void> {
  return snapshotsStore.save(items, portfolioId);
}

export const JournalEntrySchema = z.object({
  id: z.string().optional(),
  date: z.string(),
  title: z.string().optional(),
  content: z.string().optional(),
  text: z.string().optional(),                                // legacy alias
  sentiment: z.enum(['Bullish', 'Neutral', 'Bearish']).optional(),
  conviction: z.number().int().min(0).max(5).optional(),
  tickers: z.array(z.string()).optional(),
}).loose();
export type JournalEntry = z.infer<typeof JournalEntrySchema>;

const journalStore = createScopedBlobStore('journal', JournalEntrySchema);

export function getJournal(portfolioId?: string): Promise<JournalEntry[]> {
  return journalStore.get(portfolioId);
}

export function saveJournal(items: JournalEntry[], portfolioId?: string): Promise<void> {
  return journalStore.save(items, portfolioId);
}

// Settings is global, not portfolio-scoped.
export async function saveSettings(next: Settings): Promise<void> {
  await writeKvBlob('settings', next);
}

/** Persist the full self-priced instrument price map. */
export async function saveCustomPrices(next: CustomPrices): Promise<void> {
  await writeKvBlob('customPrices', next);
}

export const ThinkPieceSchema = z.object({
  id: z.string().optional(),
  date: z.string(),
  title: z.string().optional(),
  content: z.string().optional(),
  tickers: z.array(z.string()).optional(),
  status: z.enum(['draft', 'published']).optional(),
}).loose();
export type ThinkPiece = z.infer<typeof ThinkPieceSchema>;

const thinkPiecesStore = createScopedBlobStore('thinkPieces', ThinkPieceSchema);

export function getThinkPieces(portfolioId?: string): Promise<ThinkPiece[]> {
  return thinkPiecesStore.get(portfolioId);
}

export function saveThinkPieces(items: ThinkPiece[], portfolioId?: string): Promise<void> {
  return thinkPiecesStore.save(items, portfolioId);
}

export interface OpsHealth { ok: boolean; timestamp?: string; bindings?: Record<string, boolean> }

export async function fetchOpsHealth(): Promise<OpsHealth | null> {
  return getJson<OpsHealth>('/api/ops/health', { credentials: 'include' });
}


/**
 * Fetch trades for every portfolio in settings.portfolios. Falls back to a
 * single 'default' key when the trades blob is in the legacy array shape.
 */
export async function getAllPortfolioTrades(
  portfolios: PortfolioRef[],
): Promise<Record<string, Trade[]>> {
  if (portfolios.length === 0) {
    return { default: await getTrades() };
  }
  const entries = await Promise.all(
    portfolios.map(async (p) => [p.id, await getTrades(p.id)] as const),
  );
  return Object.fromEntries(entries);
}
